// Unified notifications state.
//
// Subscribes to the shared site SSE stream for friend-request,
// game-invited, and notifications-changed pokes; refetches the
// authoritative list from /api/notifications on each. Components
// (bell-icon widget, /notifications page, banners) read the cached
// state and re-render via the listener pub/sub.

import { api } from '../lib/api.js';
import { siteEvents } from '../lib/site-events.js';

/**
 * @typedef {object} NotificationItem
 * @property {string} id
 * @property {'friend_request'|'game_invitation'|string} kind
 * @property {string} sourceId
 * @property {string} createdAt
 * @property {boolean} read
 * @property {string} [readAt]
 * @property {object} [friendRequest]
 * @property {object} [gameInvitation]
 */

export class NotificationsStateManager {
  constructor() {
    /** @type {Set<() => void>} */
    this._listeners = new Set();
    /** @type {NotificationItem[]} */
    this.notifications = [];
    /** @type {number} */
    this.unreadCount = 0;
    /** @type {boolean} */
    this.loaded = false;
    /** @type {boolean} */
    this._fetching = false;
    /** @type {Array<() => void>} */
    this._unsubscribers = [];
    this._wired = false;
  }

  /** @param {() => void} listener */
  subscribe(listener) {
    this._listeners.add(listener);
    this._wireSiteEvents();
    return () => {
      this._listeners.delete(listener);
      if (this._listeners.size === 0) this._teardownSiteEvents();
    };
  }

  _wireSiteEvents() {
    if (this._wired) return;
    this._wired = true;
    const refetch = () => this.refetch();
    this._unsubscribers.push(siteEvents.subscribe('friend-request', refetch));
    this._unsubscribers.push(siteEvents.subscribe('friendship-changed', refetch));
    this._unsubscribers.push(siteEvents.subscribe('game-invited', refetch));
    this._unsubscribers.push(siteEvents.subscribe('notifications-changed', refetch));
  }

  _teardownSiteEvents() {
    for (const u of this._unsubscribers) u();
    this._unsubscribers = [];
    this._wired = false;
  }

  async refetch() {
    if (this._fetching) return;
    this._fetching = true;
    try {
      const data = await api.getNotifications({ limit: 50 });
      // Dedupe by id (in case the server returns duplicates) and keep
      // the freshest read state.
      const byId = new Map();
      for (const n of data.notifications || []) byId.set(n.id, n);
      this.notifications = [...byId.values()];
      this.unreadCount =
        typeof data.unreadCount === 'number' ? data.unreadCount : this.notifications.filter((n) => !n.read).length;
      this.loaded = true;
      this._notify();
    } catch (err) {
      console.warn('[notifications-state] refetch failed', err);
    } finally {
      this._fetching = false;
    }
  }

  async markAllRead() {
    try {
      await api.markAllNotificationsRead();
      for (const n of this.notifications) {
        if (!n.read) {
          n.read = true;
          n.readAt = new Date().toISOString();
        }
      }
      this.unreadCount = 0;
      this._notify();
    } catch (err) {
      console.warn('[notifications-state] markAllRead failed', err);
    }
  }

  /** Pending friend requests visible in the panel. */
  get pendingFriendRequests() {
    return this.notifications.filter(
      (n) => n.kind === 'friend_request' && n.friendRequest && n.friendRequest.status === 'pending',
    );
  }

  /** Pending game invitations visible in the panel. */
  get pendingGameInvitations() {
    return this.notifications.filter(
      (n) => n.kind === 'game_invitation' && n.gameInvitation && n.gameInvitation.status === 'pending',
    );
  }

  _notify() {
    for (const l of this._listeners) {
      try {
        l();
      } catch (err) {
        console.error('[notifications-state] listener threw', err);
      }
    }
  }
}

export const notificationsState = new NotificationsStateManager();
